import {ChangeEvent, FC, useState} from "react";
import {css, cx} from "@pandacss/css";
import {font} from "../style/typography.tsx";
import {ArrowDownIcon, InfoCircleErrorIcon} from "./Icon";

export interface Option {
    value: string;
    label: string;
}

interface Props {
    name?: string;
    value?: string;
    options: Option[];
    onChange?: (value: string) => void;
    error?: boolean;
    placeholder?: string;
    disabled?: boolean;
}

const containerClass = css({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
});

const selectClass = css({
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    cursor: 'pointer',
    zIndex: 2,
    color: "initial"
})

const buttonClass = css({
    height: '36px',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    border: '1px solid',
    borderColor: 'neutral.280',
    backgroundColor: 'neutral.700',
    borderRadius: '8px',
    color: 'neutral.100',
})

const focusClass = css({
    border: '1px solid',
    borderColor: 'primary.400',
    outline: '1px solid',
    outlineColor: 'primary.400',
})

const errorClass = css({
    border: '1px solid',
    borderColor: 'error.500',
    outline: '1px solid',
    outlineColor: 'error.500',
});

const disabledClass = css({
    backgroundColor: 'neutral.350',
    borderColor: 'neutral.280',
    color: 'neutral.250',
    cursor: 'not-allowed',
})

const placeholderTextClass = css({
    color: 'neutral.280',
})

const textAreaClass = css({
    flexGrow: 1,
})

const SelectInput: FC<Props> = ({name, value, options, onChange, error, disabled, placeholder}) => {
    const selectedOption = options.find(option => option.value === value);
    const selectedText = (selectedOption ? selectedOption.label : placeholder) || 'Select an option';
    const [focused, setFocused] = useState(false);

    const handleChangeEvent = (event: ChangeEvent<HTMLSelectElement>) => {
        onChange?.(event.target.value);
    }

    return <div className={cx(containerClass, font('body', '1', 'regular'))}>
        <div className={cx(buttonClass, !selectedOption && placeholderTextClass, error && errorClass, disabled && disabledClass, focused && focusClass)}>

            <span className={textAreaClass}>{selectedText}</span>
            { error && <InfoCircleErrorIcon size={16} />}
            <ArrowDownIcon size={16} />
        </div>

        <select
                className={selectClass}
                value={value || ""}
                onChange={handleChangeEvent}
                disabled={disabled}
                name={name}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}>
            { value === undefined && <option value="" disabled>{placeholder || 'Select an option'}</option>}
            {options.map(option => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    </div>
}

export default SelectInput;